import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion, AccordionDetails, AccordionSummary, alpha, Box, Chip,
  CircularProgress, Divider, Drawer, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import { fetchRequest as fetchEdge, clearState as clearEdge } from '../../redux/actions/discovery/edge';
import { mechanismMeta, lifecycleMeta } from './mechanismMeta';
import { useDisplayNames } from '../../utils/displayNames';
import LagProfileChart from './evidence/LagProfileChart';
import EffectShapeChart from './evidence/EffectShapeChart';
import EventResponseChart from './evidence/EventResponseChart';
import FoldLiftTable from './evidence/FoldLiftTable';
import StrengthSparkline from './evidence/StrengthSparkline';
import SimilarSignalsSection from './SimilarSignalsSection';

const Section = ({ title, subtitle, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

const Metric = ({ label, value }) => (
  <Stack sx={{ minWidth: 86 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Stack>
);

const fmtDay = (iso) => (iso ? String(iso).slice(0, 10) : null);

const regimeSpanLabel = (r) => {
  const start = fmtDay(r?.start);
  const end = fmtDay(r?.end);
  if (start && end) return `${start} → ${end}`;
  if (start) return `since ${start}`;
  if (end) return `until ${end}`;
  return 'whole history';
};

/**
 * Full-evidence drawer for one relationship edge (spec §7 J3, M8).
 * Fetches the complete edge document (fold table, SHAP shape, event
 * response, provenance) when opened with an edgeId.
 */
const EdgeDrawer = ({ edgeId, onClose }) => {
  const dispatch = useDispatch();
  const edge = useSelector((s) => s.discovery_edge?.response?.edge);
  const isLoading = useSelector((s) => Boolean(s.discovery_edge?.isLoading));
  const nameOf = useDisplayNames();

  useEffect(() => {
    if (edgeId) {
      dispatch(fetchEdge({ edgeId }));
    }
    return () => { dispatch(clearEdge()); };
  }, [edgeId, dispatch]);

  const mech = mechanismMeta(edge?.mechanism);
  const life = lifecycleMeta(edge?.lifecycle);
  const lift = edge?.strength?.lift;
  const isEvent = edge?.source?.kind === 'event';
  const isExploratory = edge?.lifecycle === 'exploratory';

  // Why this relationship is only suggestive, in plain words — every
  // exploratory edge cleared the lift test (positive lift) but missed the
  // validation bar on either false-discovery control or fold consistency.
  const exploratoryReason = () => {
    const q = edge?.confidence?.q_value;
    const stab = edge?.strength?.stability;
    if (q != null && q > 0.1) {
      return `its false-discovery risk is still too high (q = ${q}, needs ≤ 0.10)`;
    }
    if (stab != null && stab < 0.6) {
      return `it didn\u2019t hold up consistently across folds (positive in ${Math.round(stab * 100)}% of folds, needs ≥ 60%)`;
    }
    return 'it fell just short of the validation bar';
  };

  return (
    <Drawer anchor="right" open={Boolean(edgeId)} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 440 }, maxWidth: '100vw', p: 2.5, overflowY: 'auto' }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!isLoading && edge && (
          <>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                {isEvent
                  ? <BoltIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                  : <ShowChartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, maxWidth: 220 }} title={edge.source?.key}>
                  {nameOf(edge.source?.key)}
                </Typography>
                <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="subtitle2" noWrap color="text.secondary" sx={{ maxWidth: 110 }} title={edge.target_key}>
                  {nameOf(edge.target_key)}
                </Typography>
              </Stack>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1, mb: 2 }}>
              <Chip size="small" color={life.tone === 'default' ? undefined : life.tone} label={life.label} />
              <Tooltip title={mech.description} arrow>
                <Chip size="small" variant="outlined" color={mech.tone === 'default' ? undefined : mech.tone} label={mech.label} />
              </Tooltip>
              <Chip size="small" variant="outlined" label={`${edge.direction === 'negative' ? '−' : '+'} ${edge.direction}`} />
              <Chip size="small" variant="outlined" label={`leads by ${edge.lag_bins} bin${edge.lag_bins === 1 ? '' : 's'}`} />
              {edge.source?.channel && edge.source.channel !== 'level' && (
                <Chip size="small" variant="outlined" label={`channel ${edge.source.channel}`} />
              )}
              {edge.regime_gated && (
                <Tooltip
                  arrow
                  title={`This relationship only shows up in one regime of the target's history (active ${regimeSpanLabel(edge.evidence?.regimes?.active_span)}). It still passed the pooled validation gates — the tag tells you when to trust it.`}
                >
                  <Chip size="small" color="warning" variant="outlined" label="regime-gated" />
                </Tooltip>
              )}
            </Stack>

            {isExploratory && (
              <Box
                sx={{
                  display: 'flex', gap: 1.25, alignItems: 'flex-start',
                  p: 1.5, mb: 2, borderRadius: 1.5,
                  border: (t) => `1px solid ${alpha(t.palette.secondary.main, 0.4)}`,
                  backgroundColor: (t) => alpha(t.palette.secondary.main, 0.08),
                }}
              >
                <ScienceOutlinedIcon sx={{ fontSize: 20, color: 'secondary.main', mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                    Suggestive relationship
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    This driver showed a real, positive out-of-sample lift, but {exploratoryReason()}.
                    It&rsquo;s shown for exploration and is <b>not used in forecasts</b> until it validates.
                    The evidence below is exactly what was measured — keep an eye on it across future runs.
                  </Typography>
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={2.5} sx={{ mb: 2 }}>
              <Metric
                label="Lift"
                value={lift != null ? `${(lift * 100).toFixed(1)}%` : 'pending'}
              />
              <Metric
                label="FDR q"
                value={edge.confidence?.q_value != null ? edge.confidence.q_value : 'pending'}
              />
              <Metric
                label="Stability"
                value={edge.strength?.stability != null
                  ? `${Math.round(edge.strength.stability * 100)}%`
                  : '—'}
              />
              <Metric
                label="Correlation"
                value={edge.strength?.corr != null ? edge.strength.corr : '—'}
              />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Live health (M14) — only once the live loop has sampled */}
            {(edge.rolling?.history || []).length > 0 && (
              <Section
                title="Live strength"
                subtitle="Re-measured every 15 minutes. Dashed line is the validation baseline; a red marker is a CUSUM-confirmed change-point."
              >
                <StrengthSparkline rolling={edge.rolling} />
              </Section>
            )}

            {/* Evidence */}
            <Section
              title="Lag profile"
              subtitle="How much each lead time contributes — broken one lag at a time."
            >
              <LagProfileChart
                profile={edge.evidence?.s3?.lag_profile || []}
                bestLag={edge.lag_bins}
              />
            </Section>

            <Section
              title="Effect shape"
              subtitle="Marginal effect on the forecast across the driver's range (TreeSHAP)."
            >
              <EffectShapeChart
                shape={edge.evidence?.shap_shape || []}
                direction={edge.direction}
              />
            </Section>

            {isEvent && (
              <Section
                title="Event response"
                subtitle="The target's measured average move after this type of event."
              >
                <EventResponseChart study={edge.evidence?.event_response} />
              </Section>
            )}

            <Section
              title="Validation folds"
              subtitle="Out-of-sample lift on purged walk-forward folds. ⚡ marks folds containing qualifying shocks."
            >
              <FoldLiftTable folds={edge.confidence?.folds || []} />
            </Section>

            {/* Find-similar (E9): only for signal drivers — an event edge
                already carries its own response chart, and the embedding
                space for events is keyed by event instance, not stream. */}
            {!isEvent && edge.source?.key && (
              <SimilarSignalsSection sourceKey={edge.source.key} />
            )}

            {(edge.evidence?.regimes?.per_regime || []).length > 0 && (
              <Section
                title="Lift per regime"
                subtitle="The target's history spans multiple regimes — fold lifts broken down by which regime each validation window fell in."
              >
                <Stack spacing={0.5}>
                  {edge.evidence.regimes.per_regime.map((r) => (
                    <Stack key={r.regime} direction="row" spacing={1.5} alignItems="baseline">
                      <Typography variant="caption" sx={{ fontWeight: 600, width: 140, flexShrink: 0 }}>
                        {regimeSpanLabel(r)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={edge.evidence.regimes.active_regime === r.regime ? 'warning.main' : 'text.secondary'}
                      >
                        {`${(100 * (r.lift_mean || 0)).toFixed(1)}% lift over ${r.n_folds} fold${r.n_folds === 1 ? '' : 's'}`}
                        {edge.evidence.regimes.active_regime === r.regime ? ' · active regime' : ''}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Section>
            )}

            {/* Provenance */}
            <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                <Typography variant="subtitle2">Provenance — every stage&rsquo;s verdict</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  <ProvRow
                    stage="S1 Screen"
                    detail={`corr ${edge.evidence?.screen?.corr ?? '—'} at lag ${edge.evidence?.screen?.best_lag ?? '—'} · p ${edge.evidence?.screen?.p_screen ?? edge.evidence?.screen?.p_raw ?? '—'}`}
                  />
                  <ProvRow
                    stage="S2 Prune"
                    detail={(edge.absorbed || []).length
                      ? `kept; absorbed ${edge.absorbed.length} near-duplicate${edge.absorbed.length === 1 ? '' : 's'}: ${edge.absorbed.map((a) => nameOf(a.key)).join(', ')}`
                      : 'kept; no near-duplicates absorbed'}
                  />
                  <ProvRow
                    stage="S3 Characterize"
                    detail={`probe importance ${edge.evidence?.s3?.probe_importance ?? '—'} · monotonicity ${edge.evidence?.s3?.monotonicity ?? '—'}`}
                  />
                  <ProvRow
                    stage="S4 Lift"
                    detail={edge.evidence?.s4
                      ? `mean lift ${(100 * (edge.evidence.s4.lift_mean || 0)).toFixed(2)}% over ${edge.evidence.s4.n_folds} folds · p ${edge.evidence.s4.lift_p}`
                      : 'pending validation'}
                  />
                  <ProvRow
                    stage="S5 FDR"
                    detail={edge.confidence?.q_value != null
                      ? `q = ${edge.confidence.q_value} · stability ${edge.strength?.stability ?? '—'}`
                      : 'pending validation'}
                  />
                  <ProvRow
                    stage="Run"
                    detail={edge.run_id || '—'}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>
    </Drawer>
  );
};

const ProvRow = ({ stage, detail }) => (
  <Stack direction="row" spacing={1.5} alignItems="baseline">
    <Typography variant="caption" sx={{ fontWeight: 600, width: 110, flexShrink: 0 }}>
      {stage}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
      {detail}
    </Typography>
  </Stack>
);

export default EdgeDrawer;
